import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCatalog } from '../../context/CatalogContext'
import { MENU_LANGS } from '../menu/menuI18n'
import {
  fetchTranslations,
  productSrc,
  requestTranslation,
  saveTranslation,
  type TxKind,
  type TxRow,
} from '../../services/menuTranslations'
import { ChevronDownIcon } from '../icons'
import './MenuTranslationsPanel.css'

type Lang = TxRow['lang']
const LANGS = MENU_LANGS.filter((l) => l.code !== 'tr') as { code: Lang; label: string }[]

/**
 * QR menünün çevirileri, patronun gözünden.
 *
 * Çeviriyi Google Çeviri yapıyor (kelimeyi bağlamsız çevirebiliyor: "Kola" → "Koks"); burası yanlışı düzeltmek için. Düzeltilen
 * çeviri "elle" işaretleniyor ve Türkçesi değişene kadar yeniden
 * çevrilmiyor. Türkçesi değişmiş (eskimiş) çeviri boş görünüyor: QR menü de
 * onu göstermiyor.
 */
export function MenuTranslationsPanel({ tenantId }: { tenantId: string }) {
  const { activeCategories, productsByCategory } = useCatalog()
  const [lang, setLang] = useState<Lang>('en')
  const [rows, setRows] = useState<TxRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ text: string; bad?: boolean } | null>(null)
  /**
   * Kategori ve ürün listesi kapalı başlıyor (kullanıcı, 6 Eki 2026): hepsi
   * çevriliyken sayfayı uzatmasın; düzeltmek isteyen açıyor.
   */
  const [listOpen, setListOpen] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    fetchTranslations(tenantId, lang)
      .then(setRows)
      .catch((e: Error) => setMsg({ text: e.message, bad: true }))
      .finally(() => setLoading(false))
  }, [tenantId, lang])
  useEffect(load, [load])

  const byKey = useMemo(() => {
    const m = new Map<string, TxRow>()
    for (const r of rows) m.set(`${r.kind}:${r.item_id}`, r)
    return m
  }, [rows])

  const items = useMemo(
    () =>
      activeCategories.map((c) => ({
        cat: c,
        products: productsByCategory(c.id),
      })),
    [activeCategories, productsByCategory],
  )

  /** Yalnızca Türkçesi hala aynı olan çeviri geçerli. */
  const fresh = (kind: TxKind, id: string, src: string) => {
    const r = byKey.get(`${kind}:${id}`)
    return r && r.src === src ? r : undefined
  }

  const total = items.reduce((n, g) => n + 1 + g.products.length, 0)
  const done = items.reduce(
    (n, g) =>
      n +
      (fresh('c', g.cat.id, g.cat.name) ? 1 : 0) +
      g.products.filter((p) => fresh('p', p.id, productSrc(p.name, p.description))).length,
    0,
  )

  async function onTranslate() {
    setBusy(true)
    setMsg(null)
    const r = await requestTranslation(tenantId)
    setBusy(false)
    if (r.error) setMsg({ text: r.error, bad: true })
    else setMsg({ text: r.translated ? `${r.translated} çeviri güncellendi.` : 'Çeviriler güncel.' })
    load()
  }

  async function onSave(kind: TxKind, id: string, src: string, name: string, description: string | null) {
    const cur = fresh(kind, id, src)
    if (!name.trim()) return
    if (cur && cur.name === name.trim() && (cur.description ?? '') === (description ?? '').trim()) return
    try {
      const row = { kind, item_id: id, lang, name: name.trim(), description: description?.trim() || null, src }
      await saveTranslation(tenantId, row)
      setRows((prev) => [...prev.filter((r) => !(r.kind === kind && r.item_id === id)), { ...row, manual: true }])
      setMsg({ text: 'Kaydedildi.' })
    } catch (e) {
      setMsg({ text: (e as Error).message, bad: true })
    }
  }

  return (
    <div className="mtx">

      <div className="mtx__langs" role="tablist">
        {LANGS.map((l) => (
          <button
            key={l.code}
            type="button"
            role="tab"
            aria-selected={lang === l.code}
            className={lang === l.code ? 'is-on' : ''}
            onClick={() => setLang(l.code)}
          >
            {l.label}
          </button>
        ))}
      </div>

      {/* Hepsi çevriliyse yalnız yeşil bir satır; eksik varsa kaç tane
          olduğu ve "Şimdi çevir". Çeviri zaten kendiliğinden yapılıyor, bu
          düğme internet gittiği ya da çeviri yapılamadığı durum için. */}
      <div className={`mtx__status ${!loading && done >= total ? 'is-done' : ''}`}>
        <span>
          {loading
            ? 'Yükleniyor…'
            : done >= total
              ? `Hepsi çevrili (${total} kalem)`
              : `${total - done} kalem çevrilmedi`}
        </span>
        {!loading && done < total && (
          <button type="button" className="btn btn--outline" disabled={busy} onClick={() => void onTranslate()}>
            {busy ? 'Çevriliyor…' : 'Şimdi çevir'}
          </button>
        )}
      </div>

      <button
        type="button"
        className="mtx__toggle"
        aria-expanded={listOpen}
        onClick={() => setListOpen((v) => !v)}
      >
        <span>Kategori ve ürünler</span>
        <span className={`mtx__caret ${listOpen ? 'is-open' : ''}`}>
          <ChevronDownIcon />
        </span>
      </button>
      {msg && <p className={`mtx__msg ${msg.bad ? 'is-bad' : ''}`}>{msg.text}</p>}

      {!loading &&
        listOpen &&
        items.map(({ cat, products }) => (
          <div key={cat.id} className="mtx__group">
            <Field
              key={`c:${cat.id}:${lang}:${fresh('c', cat.id, cat.name)?.name ?? ''}`}
              className="mtx__row mtx__row--cat"
              source={cat.name}
              row={fresh('c', cat.id, cat.name)}
              dir={lang === 'ar' ? 'rtl' : 'ltr'}
              onSave={(n) => void onSave('c', cat.id, cat.name, n, null)}
            />
            {products.map((p) => {
              const src = productSrc(p.name, p.description)
              const r = fresh('p', p.id, src)
              return (
                <Field
                  key={`p:${p.id}:${lang}:${r?.name ?? ''}:${r?.description ?? ''}`}
                  className="mtx__row"
                  source={p.name}
                  sourceDesc={p.description?.trim() || null}
                  row={r}
                  dir={lang === 'ar' ? 'rtl' : 'ltr'}
                  onSave={(n, d) => void onSave('p', p.id, src, n, d)}
                />
              )
            })}
          </div>
        ))}
    </div>
  )
}

function Field({
  className,
  source,
  sourceDesc,
  row,
  dir,
  onSave,
}: {
  className: string
  source: string
  sourceDesc?: string | null
  row?: TxRow
  dir: 'rtl' | 'ltr'
  onSave: (name: string, description: string | null) => void
}) {
  const [name, setName] = useState(row?.name ?? '')
  const [desc, setDesc] = useState(row?.description ?? '')
  const save = () => onSave(name, sourceDesc ? desc : null)

  return (
    <div className={className}>
      <div className="mtx__src">
        <span>{source}</span>
        {sourceDesc && <em>{sourceDesc}</em>}
      </div>
      <div className="mtx__dst">
        <input
          value={name}
          dir={dir}
          placeholder="Çeviri yok"
          onChange={(e) => setName(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
        {sourceDesc && (
          <input
            className="mtx__desc"
            value={desc}
            dir={dir}
            placeholder="Açıklama çevirisi yok"
            onChange={(e) => setDesc(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          />
        )}
        {row?.manual && <span className="mtx__manual">elle düzeltildi</span>}
      </div>
    </div>
  )
}
